import React, { useState } from "react";
import { Field, Form, Formik } from "formik";
import { Button, Grid, Label, Modal, Segment } from "semantic-ui-react";
import { useDispatch, useSelector } from "react-redux";
import ModalWrapper from "../../../app/common/modals/ModalWrapper";
import { closeModal, openModal } from "../../../app/common/modals/modalReducer";
import MyTextInput from "../../../app/common/form/MyTextInput";
import {
  SEMANTIC_DIV,
  TEAL_BOX,
  TEAL_CONTAINER_COLOUR,
} from "../../../css/styles";
import { POST_JOB_VALIDATION_SCHEMA } from "../../../js/constants/validationSchemas";
import MySelectInput from "../../../app/common/form/MySelectInput";
import { DROP_DOWN_JOB_TYPE_OPTIONS } from "../../jobs/helpers/jobTypes";
import {
  DROP_DOWN_INDUSTRY_OPTIONS,
  HEALTHCARE_INDUSTRY,
} from "../../jobs/helpers/jobIndustries";
// import isEqual from "lodash/isEqual";
// import omit from "lodash/omit";
import {
  listenToDocFromFirestore,
  logErrorInFirestore,
  setJobActiveInFirestore,
  updateJobInFirestore,
  updateStatusLogForServiceInFirestore,
} from "../../../app/firestore/firestoreService";
import MyEditorTextInput from "../../../app/common/form/MyEditorTextInput";
import UxToggle from "../../../app/common/components/UxToggle";
import { asyncActionError } from "../../../app/async/asyncReducer";
import useFirestoreDoc from "../../../app/hooks/useFirestoreDoc";
import { JOB_DETAILS_MANDATORY_FIELDS } from "../helpers/postJobFormConstants";
import { isDescriptionCountValid } from "../helpers/validateJobFormDetails";
import {
  FIRESTORE_JOBS_COLLECTION,
  LOG_ERROR_CODES,
  LOG_STATUS_ACTIVE,
  LOG_STATUS_INACTIVE,
  LOG_STATUS_SERVICE_EDITED,
  QUERY_CODES,
} from "../../../app/firestore/firestoreConstants";
import MyCompanyWebsiteLinkInput from "../../../app/common/form/MyCompanyWebsiteLinkInput";
import { SUCCESS_COMPLETION_MODAL } from "../../../app/common/modals/ModalManager";
import MyProTagInput from "../../../app/common/form/MyProTagInput";
import { getLongDate } from "../../../js/helpers/dateHelpers";
import {
  notifyAdmins,
  SENDINBLUE_CONSTANTS,
} from "../../../app/api/sendinblueAPI";
import { DROP_DOWN_HEALTHCARE_CATEGORY_OPTIONS } from "../../modes/healthcare/jobHealthcareCategories";
import { HR_HEALTHCARE_MODE } from "../../modes/constants";
import { checkToUpdateMultiLocJobs } from "../../../js/helpers/multipleJobLocationHelpers";

export default function EditJobModal({ job }) {
  const dispatch = useDispatch();

  const { loading, error } = useSelector((state) => state.async);
  const { currentUserAccount } = useSelector((state) => state.account);
  const { responsive } = useSelector((state) => state.app);

  const [selectedJob, setSelectedJob] = useState(null);
  const [isToggling, setIsToggling] = useState(false);

  useFirestoreDoc({
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_JOBS_COLLECTION,
        id: job.id,
      }),
    data: (selectedJob) => setSelectedJob(selectedJob),
    deps: [job.id, dispatch],
    queryCode: QUERY_CODES.listenToJobFromFirestore,
  });

  function isJobDetailsValid(values) {
    return (
      JOB_DETAILS_MANDATORY_FIELDS.filter((key) => values[key] === "")
        .length === 0
    );
  }

  function isFormValid(values) {
    return (
      // validateJobContactDetails(values) &&
      isDescriptionCountValid(values.description) && isJobDetailsValid(values)
    );
  }

  // function isDescriptionEqual(values) {
  //   return (
  //     job.description.replace(/ /g, "") === values.description.replace(/ /g, "")
  //   );
  // }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }

  async function handleIsActiveToggleButton() {
    let step = 1;
    try {
      setIsToggling(true);
      // toggle will set it to switch status, log before job update
      const logStatus = selectedJob.isActive
        ? LOG_STATUS_INACTIVE
        : LOG_STATUS_ACTIVE;
      await updateStatusLogForServiceInFirestore({
        id: job.refNumber,
        date: new Date(),
        status: logStatus,
      });
      step = 2;
      await setJobActiveInFirestore(job.id, !selectedJob?.isActive);
      step = 3;
      await checkToUpdateMultiLocJobs({ job, email: currentUserAccount.email });
      setIsToggling(false);
    } catch (e) {
      await logErrorInFirestore({
        error: e.message || "Error toggling job activity",
        errorPage: "Edit job modal",
        errorCode: LOG_ERROR_CODES.toggleJobActiveInactive,
        notes: { step: step },
      });
      dispatch(
        asyncActionError({
          code: "approve-job-error",
          message: "Could not toggle active/inactive job",
        })
      );
    }
  }

  return (
    <ModalWrapper
      modalClassName={"modal-ux-wrapper"}
      style={{ paddingLeft: 0, width: !responsive.isMobile ? 900 : "98%" }}
      centered={false}
      padding={25}
    >
      {selectedJob && (
        <Segment
          basic
          style={{ ...SEMANTIC_DIV }}
          disabled={loading || isToggling || (!selectedJob && !error)}
        >
          <Grid>
            <Grid.Column width={16}>
              <h2>Edit your job post</h2>
              <hr className="solid-divider" />
              <Segment
                basic
                style={{
                  // ...SEMANTIC_DIV,
                  margin: "24px 0",
                  ...TEAL_BOX,
                  backgroundColor: TEAL_CONTAINER_COLOUR,
                  width: "95%",
                }}
                loading={loading}
              >
                <UxToggle
                  labelText={selectedJob.isActive ? "Active" : "Inactive"}
                  checked={selectedJob.isActive}
                  handleClick={handleIsActiveToggleButton}
                  isDisabled={!selectedJob.isInitialised}
                />
                <div style={{ marginTop: 10 }}>
                  {selectedJob.isActive
                    ? "Visible to Job Seekers"
                    : "Not visible to Job Seekers"}
                </div>
                {currentUserAccount.isEmployer && (
                  <div style={{ marginTop: 5 }}>
                    This toggle won't change the date your job was published.
                  </div>
                )}
              </Segment>
              <Formik
                initialValues={selectedJob}
                validationSchema={POST_JOB_VALIDATION_SCHEMA}
                onSubmit={async (values, { setSubmitting, setErrors }) => {
                  let editStep = 1;
                  try {
                    await updateJobInFirestore({
                      job: values,
                      isAdmin: currentUserAccount.isAdmin,
                    });
                    editStep = 2;
                    await checkToUpdateMultiLocJobs({
                      job,
                      email: currentUserAccount.email,
                    });
                    if (currentUserAccount.isAdmin) {
                      setSubmitting(false);
                      dispatch(closeModal());
                      return;
                    }
                    await updateStatusLogForServiceInFirestore({
                      id: job.refNumber,
                      date: new Date(),
                      status: LOG_STATUS_SERVICE_EDITED,
                    });
                    editStep = 3;
                    if (
                      currentUserAccount.isEmployer &&
                      currentUserAccount.id === selectedJob.employerId
                    ) {
                      await notifyAdmins({
                        event: SENDINBLUE_CONSTANTS.events.jobResubmitted,
                        params: {
                          env:
                            process.env.REACT_APP_RELEASE === "production"
                              ? ""
                              : "TEST ",
                          companyName: values.company,
                          jobTitle: values.title,
                        },
                      });
                      editStep = 4;
                      setSubmitting(false);
                      dispatch(
                        openModal({
                          modalType: SUCCESS_COMPLETION_MODAL,
                          modalProps: {
                            heading: "Your job post has been resubmitted",
                            awaitText:
                              "It can take up to 24 hours to be approved, pending review from our team.",
                            notificationText:
                              "You will receive an email notification when your job post is active again.",
                          },
                        })
                      );
                    }
                  } catch (error) {
                    await logErrorInFirestore({
                      error: error.message || "Error editing job",
                      errorPage: "Edit job modal",
                      errorCode: LOG_ERROR_CODES.editJob,
                      notes: {
                        step: editStep,
                        userId: currentUserAccount.id,
                        isEmployer: currentUserAccount.isEmployer,
                        employerId: job.employerId,
                        jobId: job.id,
                        jobRefNumber: job.refNumber,
                      },
                    });
                    setErrors({ auth: error.message });
                    setSubmitting(false);
                  }
                }}
              >
                {({ isSubmitting, isValid, errors, values }) => (
                  <Form
                    className="ui form"
                    style={{
                      marginRight: 20,
                      borderTop: "1px solid #C2C5C7",
                    }}
                    onKeyDown={handleKeyDown}
                  >
                    <Modal.Content
                      scrolling
                      style={{ paddingRight: 20, height: "45vh" }}
                    >
                      <div className="edit-job-job-details">
                        <span className="h2-text">Job Details</span>
                        <div className="edit-job-input-field">
                          <MyTextInput
                            className="form-input-field"
                            name="company"
                            placeholder="Company name"
                            autoComplete="off"
                            isDisabled={!currentUserAccount.isAdmin}
                            maxLength="60"
                          />
                        </div>
                        <div className="edit-job-input-field">
                          <MyTextInput
                            className="form-input-field"
                            name="title"
                            placeholder="Job title*"
                            autoComplete="off"
                            isDisabled={!currentUserAccount.isAdmin}
                            maxLength="80"
                          />
                        </div>
                        <div className="edit-job-input-field">
                          {typeof job.type === "string" ? (
                            <MySelectInput
                              className="form-input-field"
                              name="type"
                              placeholder="Job type*"
                              options={DROP_DOWN_JOB_TYPE_OPTIONS}
                              isDisabled={!currentUserAccount.isAdmin}
                            />
                          ) : (
                            <MySelectInput
                              className="form-input-field"
                              name="type"
                              placeholder="Job type*"
                              options={DROP_DOWN_JOB_TYPE_OPTIONS}
                              isDisabled={!currentUserAccount.isAdmin}
                              multiple
                            />
                          )}
                        </div>
                        <div className="edit-job-input-field">
                          <div>
                            <span className="body-bold-text">Salary: </span>
                            <span>{selectedJob.salary.display}</span>
                          </div>
                        </div>
                        <div className="edit-job-input-field">
                          <MySelectInput
                            className="form-input-field"
                            name="industry"
                            placeholder="Industry*"
                            search={values.industry === ""}
                            options={DROP_DOWN_INDUSTRY_OPTIONS}
                            isDisabled={!currentUserAccount.isAdmin}
                            overwriteValue={
                              job.mode === HR_HEALTHCARE_MODE
                                ? HEALTHCARE_INDUSTRY
                                : null
                            }
                          />
                        </div>
                        {job.mode === HR_HEALTHCARE_MODE && (
                          <div className="edit-job-input-field">
                            <MySelectInput
                              className="form-input-field"
                              name="category"
                              placeholder="Category"
                              search
                              options={DROP_DOWN_HEALTHCARE_CATEGORY_OPTIONS}
                              showError={false}
                              isClearable={true}
                              isDisabled={!currentUserAccount.isAdmin}
                            />
                          </div>
                        )}
                        <h4 style={{ marginTop: 10 }}>
                          Shift / work hours{" "}
                          <span className="body-blue-text">(optional)</span>
                        </h4>
                        <div className="edit-job-input-field"></div>
                        <h4>Add required skills / licenses (optional)</h4>
                        <div className="edit-job-input-field">
                          <MyProTagInput
                            formTags={values.skillTags}
                            name="skillTags"
                            inputWidth={"62%"}
                            maxTags={10}
                          />
                        </div>
                        <h4>Job description</h4>
                        <div className="post-job-carousel-input-area-field">
                          <Field name="description">
                            {({ field }) => (
                              <MyEditorTextInput
                                value={field.value}
                                onChange={field.onChange(field.name)}
                                text={values.description}
                              />
                            )}
                          </Field>
                        </div>
                      </div>
                      <div className="edit-job-contact-information">
                        <span className="h2-text">Contact Information</span>
                        {/* <div
                          className="body-blue-text"
                          style={
                            validateJobContactDetails(values)
                              ? { color: "#5987b1" }
                              : { color: "#db2828" }
                          }
                        >
                          Enter at least one contact method
                        </div> */}
                        <div className="edit-job-input-field">
                          <MyTextInput
                            className="form-input-field"
                            name="contactName"
                            placeholder="Contact name"
                            autoComplete="off"
                            maxLength="50"
                          />
                        </div>
                        <div className="edit-job-input-field">
                          <MyTextInput
                            className="form-input-field"
                            name="contactEmail"
                            placeholder="Email address"
                            autoComplete="off"
                            maxLength="40"
                          />
                        </div>
                        <div className="edit-job-input-field">
                          <MyTextInput
                            className="form-input-field"
                            name="phoneNumber"
                            placeholder="Phone number"
                            autoComplete="off"
                            maxLength="25"
                          />
                        </div>
                        <div className="edit-job-input-field">
                          <MyCompanyWebsiteLinkInput
                            name="contactWebsiteUrl"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="edit-job-heading-field">
                        <span className="h2-text">Job Post Expiry Date</span>
                        <MyTextInput
                          className="form-input-field form-date-input-field"
                          name="dateExpiring"
                          value={getLongDate(selectedJob.dateExpiring)}
                          autoComplete="off"
                          maxLength="50"
                          isDisabled={true}
                        />
                      </div>
                    </Modal.Content>
                    <div
                      style={{
                        padding: "25px 0",
                        borderTop: "1px solid #C2C5C7",
                      }}
                    >
                      <Grid stackable>
                        <Grid.Column width={10}>
                          <span className="semi-bold">
                            Note: Editing your job post will resubmit your job
                            for review{" "}
                          </span>
                          and it won’t be active again until approved (up to 24
                          hours).
                        </Grid.Column>
                        <Grid.Column
                          width={6}
                          style={{ paddingRight: 0 }}
                          textAlign={"center"}
                        >
                          <Button
                            className="overwrite-button cta-orange-form"
                            content="Update & Submit"
                            loading={isSubmitting}
                            disabled={
                              !isValid ||
                              isSubmitting ||
                              // (isEqual(
                              //   omit(job, "description"),
                              //   omit(values, "description")
                              // ) &&
                              //   isDescriptionEqual(values)) ||
                              !isFormValid(values)
                            }
                            type="submit"
                          />
                        </Grid.Column>
                      </Grid>
                      {errors.auth && (
                        <div style={{ paddingTop: 10 }}>
                          <Label
                            basic
                            color="grey"
                            style={{ marginBottom: 10 }}
                            content={errors.auth}
                          />
                        </div>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </Grid.Column>
          </Grid>
        </Segment>
      )}
    </ModalWrapper>
  );
}
